import { useEffect } from 'react';
import { Outlet, useNavigate, useRouterState } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { useSesion } from '../../sesion/ProveedorDeSesion';
import { PREFIJOS_PUBLICOS, RUTAS_PUBLICAS } from '../../enrutador';
import { AltaDeEmpresa } from '../../paginas/AltaDeEmpresa';
import { ElegirEmpresa } from '../../paginas/ElegirEmpresa';
import { Plataforma } from '../../paginas/Plataforma';
import { ElegirClave } from '../../paginas/ElegirClave';
import { Entrada } from '../../paginas/Entrada';
import { Cargando } from '../ui/estados';
import { BarraSuperior } from './BarraSuperior';
import { Navegacion } from './Navegacion';
import { destinoObligado } from './destino';

/**
 * Armazón de la aplicación.
 *
 * Decide qué se ve según el estado de la sesión, que son tres y no dos: mientras se
 * comprueba si hay sesión viva no se muestra ni la entrada ni la aplicación —mostrar la
 * entrada y sacarla medio segundo después es el parpadeo más molesto que puede tener un
 * sistema que se abre veinte veces por día—.
 */
export function Armazon(): React.ReactElement {
  const { estado, perfil } = useSesion();
  const { t } = useTranslation();
  const ruta = useRouterState({ select: (interno) => interno.location.pathname });
  const navegar = useNavigate();

  const tieneAlguna = (perfil?.empresas ?? []).some((empresa) => empresa.activa);
  const administra = perfil?.esAdministradorDePlataforma === true;
  const aLaPlataforma =
    estado === 'sin_empresa' && administra && (!tieneAlguna || ruta === '/plataforma');
  const esPublica =
    RUTAS_PUBLICAS.includes(ruta) || PREFIJOS_PUBLICOS.some((p) => ruta.startsWith(p));

  // La ruta acompaña a la pantalla, y **la pantalla no se le presta a quien no es**. La regla es
  // una función pura, con su prueba: quien sale deja la ruta en la puerta, `/plataforma` es sólo de
  // quien administra, y quien administra sin comercio va a la plataforma y no al alta guiada.
  const destino = esPublica ? null : destinoObligado({ estado, ruta, administra, tieneAlguna });
  useEffect(() => {
    if (destino !== null && destino !== ruta) void navegar({ to: destino });
  }, [destino, ruta, navegar]);

  // A quien llega con un enlace de invitación no se le puede pedir que entre primero:
  // justamente todavía no tiene cuenta.
  if (esPublica) return <Outlet />;

  // Mientras la ruta se corrige no se pinta nada de ella: ni un cuadro de la plataforma para quien
  // no administra. Es el caso que se vio en producción, y un parpadeo ya es demasiado.
  if (estado === 'cargando' || (destino !== null && destino !== ruta)) {
    return (
      <div className="grid min-h-dvh place-items-center">
        <Cargando />
      </div>
    );
  }

  if (estado === 'sin_sesion') return <Entrada />;
  // Con clave provisoria, antes que cualquier otra cosa: elegir la propia. Lo hace cumplir la
  // pantalla y no el servidor, a propósito: es una política para que la persona termine con una
  // clave que sólo ella conoce, no una defensa contra ella misma.
  if (perfil?.debeCambiarClave === true) return <ElegirClave />;
  if (estado === 'sin_empresa') {
    // **Quien administra la plataforma y no tiene comercio va a la plataforma**, no al alta
    // guiada. Antes caía ahí: la pantalla le pedía la razón social de «su comercio», que es lo
    // único que no tiene que cargar. Y si además tiene comercios, elige, con la plataforma a un
    // enlace de distancia.
    if (aLaPlataforma) {
      return (
        <MarcoDePlataforma>
          <Plataforma />
        </MarcoDePlataforma>
      );
    }

    // Sin ninguna empresa, el paso siguiente es fundar la propia; con varias, elegir.
    return tieneAlguna ? <ElegirEmpresa /> : <AltaDeEmpresa />;
  }

  return (
    /*
      El menú a la izquierda, pegado y con **su propio scroll**.

      Antes el menú era una columna dentro del flujo de la página: al bajar en una lista larga se
      iba con ella, y con treinta pantallas ya no entraba en el alto de la ventana. Ahora ocupa el
      alto completo, no se mueve, y lo que se desplaza es su lista —no la página—.

      La barra y el menú **no se imprimen**. Una hoja del libro mayor con la navegación estampada
      en el margen no es un libro contable: es una captura de pantalla, y quien la recibe lo nota.
    */
    <div className="flex min-h-dvh print:block">
      <aside className="sticky top-0 h-dvh shrink-0 print:hidden">
        <Navegacion />
      </aside>
      <div className="flex min-w-0 flex-1 flex-col">
        <div className="print:hidden">
          <BarraSuperior />
        </div>
        <main className="min-w-0 flex-1 px-5 py-4 print:p-0" aria-label={t('navegacion.contenido')}>
          <div className="mx-auto max-w-[86rem] print:max-w-none">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}

/**
 * El marco para administrar la plataforma sin estar parado en ningún comercio.
 *
 * Es la barra de arriba y el contenido, **sin el menú**: el menú es del comercio —sus pantallas,
 * sus permisos— y acá no hay comercio. Cuando quien administra también es miembro de uno, entra
 * por el menú normal y ve la misma pantalla con el menú al lado.
 */
function MarcoDePlataforma({
  children,
}: {
  readonly children: React.ReactNode;
}): React.ReactElement {
  const { t } = useTranslation();
  return (
    <div className="flex min-h-dvh flex-col">
      <BarraSuperior sinComercio />
      <main className="min-w-0 flex-1 px-5 py-4" aria-label={t('navegacion.contenido')}>
        <div className="mx-auto max-w-[86rem]">{children}</div>
      </main>
    </div>
  );
}
